import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import {
  IconFolder,
  IconFile,
  IconFlask,
  IconDownload,
  IconAlertCircle,
  IconGitBranch,
  IconHistory,
  IconCornerLeftUp,
  IconBook,
} from '@tabler/icons-react';
import { api, LORE_PREFIX, type LoreFileEntry, type LoreRevision } from '../api.js';
import { AssetPreview } from '../dam/AssetPreview.js';
import { DAM_PREVIEWABLE_EXTS, extOf, basename } from '../lib/fileKinds.js';
import { relativeTime, formatSize } from '../lib/format.js';

/**
 * Lore VCS pilot browser (task #1) — read-only.
 *
 * Deliberately lean: no facets, tags, locks, search, or bulk actions
 * (that chrome is p4-coupled). URL model mirrors the depot page:
 *   /lore                      → repo list
 *   /lore/<repo>/<dir path>    → directory listing
 *   ?file=<encoded //_lore/…>  → file modal
 * The dashboard-side path for everything is the synthetic
 * `//_lore/<repo>/<path>` prefix, which flows through the shared dam
 * preview pipeline unchanged.
 */

function urlToLorePath(pathname: string): string | null {
  const sub = pathname.replace(/^\/lore\/?/, '').replace(/\/+$/, '');
  return sub ? `${LORE_PREFIX}${sub}` : null; // null = repo-list root
}

function lorePathToUrl(lorePath: string): string {
  return `/lore/${lorePath.slice(LORE_PREFIX.length)}`;
}

export default function LorePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const lorePath = urlToLorePath(location.pathname);
  const modalFile = useMemo(
    () => new URLSearchParams(location.search).get('file'),
    [location.search],
  );

  return (
    <div className="flex flex-col gap-4">
      <ReadOnlyBanner />
      <LoreBreadcrumbs lorePath={lorePath} />
      {lorePath === null ? <RepoList /> : <LoreDirectory lorePath={lorePath} />}

      <Dialog
        open={modalFile !== null}
        onOpenChange={(open) => {
          if (!open) navigate(location.pathname, { replace: true });
        }}
      >
        <DialogContent className="max-w-4xl">
          {modalFile && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-sm">
                  {basename(modalFile)}
                </DialogTitle>
              </DialogHeader>
              <LoreFileView lorePath={modalFile} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReadOnlyBanner() {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
      <IconFlask size={14} />
      Lore — read-only browse. Dashboard write operations aren't supported yet;
      commit via the lore CLI or UEFN.
    </div>
  );
}

/**
 * Repo / path crumbs with the branch pill, mirroring GitHub's header
 * row. The repo segment is emphasised the way GitHub emphasises the
 * repo name; deeper segments stay muted.
 */
function LoreBreadcrumbs({ lorePath }: { lorePath: string | null }) {
  const segments = lorePath ? lorePath.slice(LORE_PREFIX.length).split('/') : [];
  return (
    <nav className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
      {segments.length > 0 && (
        <span
          className="mr-1 inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs"
          title="Lore repositories have a single mainline branch at v0.8"
        >
          <IconGitBranch size={12} />
          main
        </span>
      )}
      <Link to="/lore" className="hover:text-foreground hover:underline">
        lore
      </Link>
      {segments.map((seg, i) => {
        const to = `/lore/${segments.slice(0, i + 1).join('/')}`;
        const last = i === segments.length - 1;
        return (
          <span key={to} className="flex items-center gap-1">
            <span>/</span>
            {last ? (
              <span className={i === 0 ? 'font-semibold text-foreground' : 'text-foreground'}>
                {seg}
              </span>
            ) : (
              <Link
                to={to}
                className={
                  i === 0
                    ? 'font-semibold text-theia-400 hover:underline'
                    : 'text-theia-400 hover:underline'
                }
              >
                {seg}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function RepoList() {
  const q = useQuery({ queryKey: ['lore.repos'], queryFn: api.loreRepos, retry: false });
  if (q.isLoading) return <Spinner />;
  if (q.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>
          Lore pilot unavailable: {(q.error as Error).message}
        </AlertDescription>
      </Alert>
    );
  }
  const repos = q.data?.repos ?? [];
  return (
    <div className="flex flex-col gap-2">
      {repos.length === 0 && (
        <p className="text-sm text-muted-foreground">No Lore repositories yet.</p>
      )}
      {repos.map((r) => (
        <Link
          key={r.name}
          to={lorePathToUrl(r.path)}
          className="flex items-start gap-3 rounded-md border border-border bg-card px-4 py-3 hover:border-theia-600"
        >
          <IconBook size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <div className="font-medium text-theia-400">{r.name}</div>
            {r.description && (
              <div className="mt-0.5 text-xs text-muted-foreground">{r.description}</div>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

/**
 * "Connect your workstation" — mints the caller a personal Lore authn
 * token (60d) and shows the ready-to-paste `lore login` command. The
 * token authenticates the CLI *and* the UE editor plugin (they share
 * the OS credential store). Exported for reuse on the Lore
 * Getting Started page.
 */
export function ConnectCard() {
  const mint = useMutation({ mutationFn: api.loreToken });
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 rounded-md border border-border bg-card px-4 py-3">
      <div className="mb-1 text-sm font-medium">Connect your workstation</div>
      <p className="mb-2 text-xs text-muted-foreground">
        Generates a personal access token (60 days) tied to your p4 account.
        Run the command once — the Lore CLI and the Unreal editor share the
        stored credential.
      </p>
      {mint.data ? (
        <div className="flex flex-col gap-2">
          <code className="block overflow-x-auto whitespace-nowrap rounded bg-muted/40 px-2 py-1.5 font-mono text-[11px]">
            {mint.data.loginCommand}
          </code>
          <div className="flex items-center gap-3 text-xs">
            <button
              type="button"
              className="text-theia-400 hover:underline"
              onClick={() => {
                void navigator.clipboard.writeText(mint.data.loginCommand);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? 'Copied!' : 'Copy command'}
            </button>
            <span className="text-muted-foreground">
              expires in {mint.data.expiresInDays} days — treat it like a password
            </span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => mint.mutate()}
          disabled={mint.isPending}
          className="rounded-md border border-theia-600 px-3 py-1 text-xs text-theia-400 hover:bg-theia-600/10"
        >
          {mint.isPending ? 'Generating…' : 'Generate token'}
        </button>
      )}
      {mint.error && (
        <p className="mt-2 text-xs text-red-400">{(mint.error as Error).message}</p>
      )}
    </div>
  );
}


/**
 * The commit bar above the file table — GitHub's most recognisable
 * element. One /api/lore/history call per repo, independent of tree
 * size. Per-ROW commit columns (which GitHub also has) are deliberately
 * absent: Lore has no bulk "last change per path" query, so they'd cost
 * a CLI spawn per row. The right column carries file size instead.
 */
function CommitBar({ repo, latest }: { repo: string; latest: LoreRevision | undefined }) {
  if (!latest) return null;
  const author = latest.author ?? '';
  return (
    <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 text-xs">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-theia-600/20 text-[9px] font-semibold uppercase text-theia-400"
        title={author}
      >
        {author.slice(0, 2) || '—'}
      </span>
      {author && <span className="shrink-0 font-medium text-foreground">{author}</span>}
      <span className="min-w-0 flex-1 truncate text-muted-foreground" title={latest.message}>
        {latest.message || '(no message)'}
      </span>
      <Link
        to={`/lore/${repo}?history=1`}
        className="shrink-0 font-mono text-[11px] text-muted-foreground hover:text-theia-400 hover:underline"
        title={latest.signature}
      >
        {latest.signature.slice(0, 7)}
      </Link>
      <span className="shrink-0 text-muted-foreground">{relativeTime(latest.time)}</span>
      <span className="hidden shrink-0 items-center gap-1 text-muted-foreground sm:flex">
        <IconHistory size={13} />
        {latest.revision} {latest.revision === 1 ? 'revision' : 'revisions'}
      </span>
    </div>
  );
}

function LoreDirectory({ lorePath }: { lorePath: string }) {
  const location = useLocation();
  const q = useQuery({
    queryKey: ['lore.list', lorePath],
    queryFn: () => api.loreList(lorePath),
    retry: false,
  });
  const repo = lorePath.slice(LORE_PREFIX.length).split('/')[0] ?? '';
  const hist = useQuery({
    queryKey: ['lore.history', repo],
    queryFn: () => api.loreHistory(repo, 1),
    retry: false,
    enabled: repo !== '',
    staleTime: 60_000,
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{(q.error as Error).message}</AlertDescription>
      </Alert>
    );
  }
  const { dirs = [], files = [] } = q.data ?? {};
  // Directories before files, each A-Z — the ordering every code host uses.
  const sortedDirs = [...dirs].sort((a, b) => basename(a.path).localeCompare(basename(b.path)));
  const sortedFiles = [...files].sort((a, b) =>
    basename(a.depotFile).localeCompare(basename(b.depotFile)),
  );
  // Parent link, except at a repo root (where the crumb goes to the repo list).
  const segments = lorePath.slice(LORE_PREFIX.length).split('/');
  const parentUrl = segments.length > 1 ? `/lore/${segments.slice(0, -1).join('/')}` : null;

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <CommitBar repo={repo} latest={hist.data?.revisions[0]} />
      {sortedDirs.length === 0 && sortedFiles.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-muted-foreground">
          This directory is empty.
        </p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {parentUrl && (
              <tr className="border-b border-border/50 hover:bg-muted/40">
                <td colSpan={2} className="px-3 py-1.5">
                  <Link to={parentUrl} className="flex items-center gap-2 text-muted-foreground">
                    <IconCornerLeftUp size={16} className="shrink-0" />
                    <span>..</span>
                  </Link>
                </td>
              </tr>
            )}
            {sortedDirs.map((d) => (
              <tr key={d.path} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                <td className="px-3 py-1.5">
                  <Link
                    to={lorePathToUrl(d.path)}
                    className="flex items-center gap-2 hover:text-theia-400 hover:underline"
                  >
                    <IconFolder size={16} className="shrink-0 fill-theia-400/20 text-theia-400" />
                    <span>{basename(d.path)}</span>
                  </Link>
                </td>
                <td className="w-28 px-3 py-1.5" />
              </tr>
            ))}
            {sortedFiles.map((f) => (
              <LoreFileRow key={f.depotFile} file={f} pathname={location.pathname} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LoreFileRow({ file, pathname }: { file: LoreFileEntry; pathname: string }) {
  return (
    <tr className="border-b border-border/50 last:border-0 hover:bg-muted/40">
      <td className="px-3 py-1.5">
        <Link
          to={{ pathname, search: `?file=${encodeURIComponent(file.depotFile)}` }}
          className="flex items-center gap-2 hover:text-theia-400 hover:underline"
        >
          <IconFile size={16} className="shrink-0 text-muted-foreground" />
          <span>{basename(file.depotFile)}</span>
        </Link>
      </td>
      <td className="w-28 px-3 py-1.5 text-right text-xs tabular-nums text-muted-foreground">
        {file.fileSize !== null ? formatSize(file.fileSize) : ''}
      </td>
    </tr>
  );
}

function LoreFileView({ lorePath }: { lorePath: string }) {
  const fstat = useQuery({
    queryKey: ['lore.fstat', lorePath],
    queryFn: () => api.loreFstat(lorePath),
    retry: false,
  });
  if (fstat.isLoading) return <Spinner />;
  if (fstat.error || !fstat.data) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>
          {fstat.error ? (fstat.error as Error).message : 'file not found'}
        </AlertDescription>
      </Alert>
    );
  }
  const { depotFile, headRev, fileSize, headTime } = fstat.data;
  const filename = basename(depotFile);
  const previewable = DAM_PREVIEWABLE_EXTS.has(extOf(filename));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>rev #{headRev}</span>
        {fileSize !== null && <span>{formatSize(fileSize)}</span>}
        {headTime && <span>{new Date(headTime).toLocaleString()}</span>}
        <a
          href={api.fileUrl(depotFile, headRev)}
          download={filename}
          className="flex items-center gap-1 text-theia-400 hover:underline"
        >
          <IconDownload size={14} /> download
        </a>
      </div>
      {previewable ? (
        <AssetPreview
          depotPath={depotFile}
          headRev={headRev}
          filename={filename}
          fileSize={fileSize}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          No preview for this file type — use the download link above.
        </p>
      )}
    </div>
  );
}

